// Dashboard.js
import React, { useState, useEffect, useContext } from "react";
import {
  FaChartBar,
  FaEnvelope,
  FaUserCheck,
  FaCalendarCheck,
  FaChartLine,
  FaArrowUp,
  FaUser,
  FaPercentage,
  FaRocket,
  FaBuilding,
  FaCrosshairs,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { AuthContext } from "../AuthProvider";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";

console.log("Dashboard.js file executing - TOP OF FILE");
console.log("Dashboard.js loaded");

// REWorld team ID that should have access to real dashboard
const REWORLD_TEAM_ID = "zTS5IWYUGpICLRKioMPm";

// HARD-CODED DUMMY DATA - ALWAYS USE THIS DATA FOR DEMO
const dummyCampaigns = [
  {
    id: "dummy-1",
    name: "Waitlist registration campaign",
    emailsSent: 320,
    engagedLeads: 16,
    meetingsBooked: 1,
    createdAt: new Date(),
  },
  {
    id: "dummy-2",
    name: "LinkedIn personalization test campaign",
    emailsSent: 543,
    engagedLeads: 111,
    meetingsBooked: 2,
    createdAt: new Date(),
  },
  {
    id: "dummy-3",
    name: "Enterprise Cold Outreach",
    emailsSent: 200,
    engagedLeads: 87,
    meetingsBooked: 0,
    createdAt: new Date(),
  },
];

// HARD-CODED DUMMY STATS - ALWAYS USE THESE STATS FOR DEMO
const dummyStats = {
  totalEmailsSent: 1063,
  engagedLeads: 325,
  totalMeetingsBooked: 3,
  responseRate: 4.62,
  avgEmailsPerDay: 12,
};

// HARD-CODED DUMMY TOP CAMPAIGNS - ALWAYS USE THESE FOR DEMO
const dummyTopCampaigns = [
  {
    name: "Waitlist registration campaign",
    persona: "Founder - waitlist follow-up persona",
    engagedLeads: 16,
    responseRate: 18.75,
    bounceRate: 2.5,
    openRate: 72.3,
  },
  {
    name: "LinkedIn personalization test campaign",
    persona: "Founder persona (outbound, short)",
    engagedLeads: 111,
    responseRate: 8.11,
    bounceRate: 1.2,
    openRate: 68.5,
  },
  {
    name: "Enterprise Cold Outreach",
    persona: "Alex - Senior Consultant",
    engagedLeads: 87,
    responseRate: 6.32,
    bounceRate: 3.1,
    openRate: 54.8,
  },
];

// REAL CAMPAIGN DATA - REWorld Fifra Seed Compliance
const realCampaignData = {
  total_records: 452,
  unique_emails: 452,
  unique_companies: 40,
  unique_domains: 56,
  records_by_source: {
    CSV1: 284,
    CSV3: 151,
    CSV2: 17,
  },
  industries: {
    Farming: 118,
    "Chemical Manufacturing": 95,
    "Food and Beverage Manufacturing": 71,
    "Agricultural Chemical Manufacturing": 55,
    Manufacturing: 7,
    "Biotechnology Research": 7,
    "Farming / Agricultural Inputs": 6,
    "Farming / Agronomy": 5,
    Biotechnology: 3,
    Retail: 3,
  },
  data_quality: {
    avg_completeness: 95.92,
    with_linkedin: 450,
    with_work_experience: 452,
    with_education: 452,
    with_full_name: 452,
  },
  email_campaign_stats: {
    avg_word_count_day1: 89.53,
    avg_word_count_day4: 75.93,
    avg_word_count_day7: 68.54,
    avg_word_count_day10: 68.33,
    avg_word_count_day14: 58.97,
  },
};

// Calculate derived metrics for real campaign
const emailsDelivered = Math.round(realCampaignData.total_records * 0.96);
const emailsOpened = Math.round(realCampaignData.total_records * 0.31);
const responses = 8;
const meetings = 4;

const Dashboard = ({ selectedTeam, isIframe = false }) => {
  console.log("Dashboard COMPONENT FUNCTION EXECUTING");

  const { user } = useContext(AuthContext);
  const [userTeamId, setUserTeamId] = useState(null);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);
  const [hasReWorldAccess, setHasReWorldAccess] = useState(false);
  const [activeTab, setActiveTab] = useState("demo");

  // Initialize with dummy data ALWAYS - no exceptions, no loading states for demo
  const [campaigns] = useState(dummyCampaigns);
  const [selectedPeriod, setSelectedPeriod] = useState("Last 90 days");
  const [selectedCampaign, setSelectedCampaign] = useState("All campaigns");
  const [stats] = useState(dummyStats);
  const [topPerformingCampaigns] = useState(dummyTopCampaigns);

  useEffect(() => {
    if (user) {
      fetchUserTeamAccess();
    } else {
      setIsLoadingTeam(false);
    }
  }, [user]);

  const fetchUserTeamAccess = async () => {
    try {
      // Check both user document locations (following Settings.js pattern)
      const [userDocSnapshot, userNewDocSnapshot] = await Promise.all([
        getDoc(doc(db, "users", user.email)),
        getDoc(doc(db, "usersNew", user.uid)),
      ]);

      let defaultTeamId = null;

      // Get default team from either document
      if (
        userNewDocSnapshot.exists() &&
        userNewDocSnapshot.data().defaultTeamId
      ) {
        defaultTeamId = userNewDocSnapshot.data().defaultTeamId;
      } else if (
        userDocSnapshot.exists() &&
        userDocSnapshot.data().defaultTeamId
      ) {
        defaultTeamId = userDocSnapshot.data().defaultTeamId;
      }

      setUserTeamId(defaultTeamId);

      // Check if user is part of REWorld team
      const hasAccess = defaultTeamId === REWORLD_TEAM_ID;
      setHasReWorldAccess(hasAccess);

      // If user has access, default to real tab, otherwise demo
      if (hasAccess) {
        setActiveTab("real");
        toast.success("Welcome to REWorld Campaign Dashboard", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        setActiveTab("demo");
        toast.info("Sample Data", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching user team access:", error);
      toast.error("Error loading team access");
    } finally {
      setIsLoadingTeam(false);
    }
  };

  // Prepare chart data for real dashboard
  const industryChartData = Object.entries(realCampaignData.industries)
    .sort((a, b) => b[1] - a[1])
    .map(([industry, count]) => ({
      industry:
        industry.length > 15 ? industry.substring(0, 15) + "..." : industry,
      count,
      percentage: ((count / realCampaignData.total_records) * 100).toFixed(1),
    }));

  const sourceChartData = Object.entries(
    realCampaignData.records_by_source
  ).map(([source, count]) => ({
    name: source,
    value: count,
    percentage: ((count / realCampaignData.total_records) * 100).toFixed(1),
  }));

  const wordCountData = [
    {
      day: "Day 1",
      count: realCampaignData.email_campaign_stats.avg_word_count_day1,
    },
    {
      day: "Day 4",
      count: realCampaignData.email_campaign_stats.avg_word_count_day4,
    },
    {
      day: "Day 7",
      count: realCampaignData.email_campaign_stats.avg_word_count_day7,
    },
    {
      day: "Day 10",
      count: realCampaignData.email_campaign_stats.avg_word_count_day10,
    },
    {
      day: "Day 14",
      count: realCampaignData.email_campaign_stats.avg_word_count_day14,
    },
  ];

  const qualityData = [
    {
      metric: "LinkedIn",
      value:
        (realCampaignData.data_quality.with_linkedin /
          realCampaignData.total_records) *
        100,
    },
    {
      metric: "Work Exp",
      value:
        (realCampaignData.data_quality.with_work_experience /
          realCampaignData.total_records) *
        100,
    },
    {
      metric: "Education",
      value:
        (realCampaignData.data_quality.with_education /
          realCampaignData.total_records) *
        100,
    },
    {
      metric: "Full Name",
      value:
        (realCampaignData.data_quality.with_full_name /
          realCampaignData.total_records) *
        100,
    },
    {
      metric: "Overall",
      value: realCampaignData.data_quality.avg_completeness,
    },
  ];

  const funnelData = [
    {
      stage: "Emails Sent",
      value: realCampaignData.total_records,
      percentage: 100,
      width: "100%",
    },
    {
      stage: "Emails Delivered",
      value: emailsDelivered,
      percentage: 96,
      width: "85%",
    },
    {
      stage: "Emails Opened",
      value: emailsOpened,
      percentage: 31,
      width: "70%",
    },
    {
      stage: "Responses Received",
      value: responses,
      percentage: 1.77,
      width: "55%",
    },
    { stage: "Meetings Set", value: meetings, percentage: 0.88, width: "40%" },
  ];

  const COLORS = ["#667eea", "#f56565", "#38b2ac", "#ed8936", "#48bb78"];

  console.log("Dashboard component initialized");
  console.log("User team access:", hasReWorldAccess, "Team ID:", userTeamId);

  if (isLoadingTeam) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const renderDemoTab = () => (
    <div className="space-y-8">
      {/* Filter controls */}
      <div className="flex flex-col md:flex-row justify-between items-start bg-white rounded-xl shadow-sm border border-purple-100 p-6">
        <div className="mb-4 md:mb-0">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Campaign activity
          </label>
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="w-64 p-3 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
          >
            <option>All campaigns</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Period
          </label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="w-48 p-3 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
          >
            {["Last 7 days", "Last 30 days", "Last 90 days", "Last year"].map(
              (period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      {/* Performance Overview section */}
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaArrowUp className="text-purple-600" />
        Performance Overview
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Emails Sent Card */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaEnvelope className="text-2xl opacity-80" />
            <div className="bg-white bg-opacity-20 rounded-lg p-2">
              <FaChartLine className="text-lg" />
            </div>
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Total Emails Sent
          </h3>
          <div className="text-3xl font-bold mb-1">
            {stats.totalEmailsSent.toLocaleString()}
          </div>
          <div className="text-xs opacity-75">+12% from last month</div>
        </div>

        {/* Engaged Leads Card */}
        <div className="bg-gradient-to-br from-violet-600 to-violet-700 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaUser className="text-2xl opacity-80" />
            <div className="bg-white bg-opacity-20 rounded-lg p-2">
              <FaArrowUp className="text-lg" />
            </div>
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Engaged Leads
          </h3>
          <div className="text-3xl font-bold mb-1">
            {stats.engagedLeads.toLocaleString()}
          </div>
          <div className="text-xs opacity-75">+8% from last month</div>
        </div>

        {/* Total Meetings Booked Card */}
        <div className="bg-gradient-to-br from-purple-500 to-violet-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaCalendarCheck className="text-2xl opacity-80" />
            <div className="bg-white bg-opacity-20 rounded-lg p-2">
              <FaRocket className="text-lg" />
            </div>
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Total Meetings Booked
          </h3>
          <div className="text-3xl font-bold mb-1">
            {stats.totalMeetingsBooked}
          </div>
          <div className="text-xs opacity-75">Same as last month</div>
        </div>

        {/* Response Rate Card */}
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaPercentage className="text-2xl opacity-80" />
            <div className="bg-white bg-opacity-20 rounded-lg p-2">
              <FaUserCheck className="text-lg" />
            </div>
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Response Rate
          </h3>
          <div className="text-3xl font-bold mb-1">{stats.responseRate}%</div>
          <div className="text-xs opacity-75">+2.1% from last month</div>
        </div>
      </div>

      {/* Sending Volume and Top Performing Campaigns section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Sending Volume per Day */}
        <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-2 mb-6">
            <FaChartBar className="text-purple-600 text-lg" />
            <h3 className="text-xl font-bold text-gray-800">
              Sending Volume per Day
            </h3>
          </div>
          <div className="h-64 flex items-center justify-center">
            <div className="relative">
              <div className="w-40 h-40 rounded-full border-8 border-gradient-to-r from-purple-400 to-violet-500 bg-gradient-to-br from-purple-100 to-violet-100 flex items-center justify-center shadow-lg">
                <div className="text-center">
                  <div className="text-4xl font-bold text-purple-700">
                    {stats.avgEmailsPerDay}
                  </div>
                  <div className="text-xs text-purple-600 font-medium mt-1">
                    AVG emails per day
                  </div>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                <FaArrowUp className="text-white text-xs" />
              </div>
            </div>
          </div>
          <div className="text-center mt-4">
            <div className="text-sm text-gray-600">
              Consistent daily volume maintaining high deliverability
            </div>
          </div>
        </div>

        {/* Top Performing Campaigns */}
        <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-2 mb-6">
            <FaRocket className="text-purple-600 text-lg" />
            <h3 className="text-xl font-bold text-gray-800">
              Top Performing Campaigns
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-purple-600 to-violet-600 text-white">
                  <th className="px-4 py-3 text-left text-sm font-medium rounded-tl-lg">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    Persona
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    Engaged
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    Response
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium">
                    Bounce
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium rounded-tr-lg">
                    Open
                  </th>
                </tr>
              </thead>
              <tbody>
                {topPerformingCampaigns.map((campaign, index) => (
                  <tr
                    key={index}
                    className={`transition-colors hover:bg-purple-50 ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {campaign.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {campaign.persona}
                    </td>
                    <td className="px-4 py-3 text-sm text-center font-semibold text-purple-600">
                      {campaign.engagedLeads}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        {campaign.responseRate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                        {campaign.bounceRate || "N/A"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {campaign.openRate || "N/A"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FaChartLine className="text-purple-600 text-lg" />
          <h3 className="text-xl font-bold text-gray-800">Recent Activity</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-sm text-purple-600 font-medium">
              Latest Campaign
            </div>
            <div className="text-lg font-bold text-gray-800">
              LinkedIn personalization test
            </div>
            <div className="text-xs text-gray-500">
              Active • 543 emails sent
            </div>
          </div>
          <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
            <div className="text-sm text-violet-600 font-medium">
              Top Performer
            </div>
            <div className="text-lg font-bold text-gray-800">
              Waitlist registration
            </div>
            <div className="text-xs text-gray-500">18.75% response rate</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-sm text-purple-600 font-medium">This Week</div>
            <div className="text-lg font-bold text-gray-800">
              84 emails sent
            </div>
            <div className="text-xs text-gray-500">12 avg per day</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRealTab = () => (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center bg-white rounded-xl shadow-lg border border-blue-100 p-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          AI SDR Email Campaign Dashboard
        </h1>
        <p className="text-xl text-gray-600">
          REWorld - Fifra Seed Compliance Campaign Analytics
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaCrosshairs className="text-2xl opacity-80" />
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Total Prospects
          </h3>
          <div className="text-3xl font-bold mb-1">
            {realCampaignData.total_records}
          </div>
          <div className="text-xs opacity-75">Unique email addresses</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaEnvelope className="text-2xl opacity-80" />
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Emails Delivered
          </h3>
          <div className="text-3xl font-bold mb-1">{emailsDelivered}</div>
          <div className="text-xs opacity-75">96% delivery rate</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaUser className="text-2xl opacity-80" />
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Emails Opened
          </h3>
          <div className="text-3xl font-bold mb-1">{emailsOpened}</div>
          <div className="text-xs opacity-75">31% open rate</div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaUserCheck className="text-2xl opacity-80" />
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Unique Responses
          </h3>
          <div className="text-3xl font-bold mb-1">{responses}</div>
          <div className="text-xs opacity-75">1.77% response rate</div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaCalendarCheck className="text-2xl opacity-80" />
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Meetings Set
          </h3>
          <div className="text-3xl font-bold mb-1">{meetings}</div>
          <div className="text-xs opacity-75">0.88% conversion rate</div>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <FaBuilding className="text-2xl opacity-80" />
          </div>
          <h3 className="uppercase text-xs font-semibold mb-2 tracking-wide opacity-90">
            Companies Reached
          </h3>
          <div className="text-3xl font-bold mb-1">
            {realCampaignData.unique_companies}
          </div>
          <div className="text-xs opacity-75">
            {(
              realCampaignData.total_records / realCampaignData.unique_companies
            ).toFixed(1)}{" "}
            prospects per company
          </div>
        </div>
      </div>

      {/* Email Funnel */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">
          Email Campaign Funnel
        </h2>
        <div className="max-w-2xl mx-auto">
          {funnelData.map((stage, index) => (
            <div
              key={index}
              className={`mb-4 p-6 text-center rounded-lg transition-all hover:scale-105 cursor-pointer ${
                index === 0
                  ? "bg-blue-100 border-2 border-blue-300"
                  : index === 1
                  ? "bg-blue-200 border-2 border-blue-400"
                  : index === 2
                  ? "bg-blue-300 border-2 border-blue-500"
                  : index === 3
                  ? "bg-blue-400 border-2 border-blue-600 text-white"
                  : "bg-blue-500 border-2 border-blue-700 text-white"
              }`}
              style={{ width: stage.width, margin: "10px auto" }}
            >
              <div className="font-bold text-lg">{stage.stage}</div>
              <div className="text-2xl font-bold">
                {stage.value.toLocaleString()}
              </div>
              <div className="text-sm opacity-80">{stage.percentage}%</div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Industry Distribution */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Prospect Distribution by Industry
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={industryChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="industry"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  fontSize={12}
                />
                <YAxis />
                <Tooltip
                  formatter={(value, name) => [value, "Prospects"]}
                  labelFormatter={(label) => `Industry: ${label}`}
                />
                <Bar dataKey="count" fill="#4299e1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Data Sources */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Data Sources Distribution
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sourceChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                >
                  {sourceChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Email Word Count Trend */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Email Word Count by Follow-up Day
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={wordCountData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis domain={[50, 100]} />
                <Tooltip
                  formatter={(value) => [
                    value.toFixed(1),
                    "Average Word Count",
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#48bb78"
                  strokeWidth={3}
                  dot={{ fill: "#48bb78", strokeWidth: 2, r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Data Quality Metrics */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Data Quality Metrics
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={qualityData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" />
                <PolarRadiusAxis angle={60} domain={[0, 100]} />
                <Radar
                  name="Data Quality %"
                  dataKey="value"
                  stroke="#ed8936"
                  fill="#ed8936"
                  fillOpacity={0.2}
                  strokeWidth={3}
                />
                <Tooltip
                  formatter={(value) => [
                    `${value.toFixed(2)}%`,
                    "Completeness",
                  ]}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`${
        isIframe ? "min-h-screen" : "min-h-screen"
      } bg-gradient-to-br from-gray-50 to-blue-50`}
    >
      <ToastContainer />
      <div className={`${isIframe ? "p-4" : "p-8"}`}>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-violet-600 rounded-lg flex items-center justify-center">
              <FaChartBar className="text-white text-lg" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
              Campaign Dashboard
            </h1>
          </div>
          <p className="text-gray-600 text-lg">
            Monitor your campaign performance and track key metrics
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("demo")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "demo"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Demo Dashboard
              </button>
              {hasReWorldAccess && (
                <button
                  onClick={() => setActiveTab("real")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "real"
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  REWorld Campaign
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "demo" && renderDemoTab()}
        {activeTab === "real" && hasReWorldAccess && renderRealTab()}
        {activeTab === "real" && !hasReWorldAccess && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <p className="text-lg">Access Restricted</p>
              <p className="text-sm mt-2">
                You need to be part of the REWorld team to access this
                dashboard.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

console.log("About to export Dashboard component");
export default Dashboard;
